import React, { useState, useEffect, useMemo } from 'react';
import LeaderboardTableHeaderRow from '../LeaderboardTableHeaderRow/LeaderboardTableHeaderRow';
import LeaderboardTableRow from '../LeaderboardTableRow/LeaderboardTableRow';
import PeriodFilter from '../PeriodFilter/PeriodFilter';
import StatFilter from '../StatFilter/StatFilter';
import Pagination from '../Pagination/Pagination';
import { getClanLeaderboard } from '../../client/api/server_api';
import { LEADERBOARD_COLUMNS, PERIODS, CLANS_PER_PAGE, FILTERS } from '../../metadata/fields';
import { useParamsState } from '../../metadata/hooks.js';

const STRINGS = {
    REGIMENT_LEADERBOARD: gettext("REGIMENT LEADERBOARD"),
    JUMP_TO_CLAN: gettext("Jump to My Regiment"),
}

function LeaderboardTable() {
    const [clans, setClans] = useState([]);
    const [myClan, setMyClan] = useState(null);
    const [myClanPage, setMyClanPage] = useState(null);
    const [sortColumn, setSortColumn] = useParamsState('column', 'avg_global_rating');
    const [sortAscending, setSortAscending] = useParamsState('asc', false);
    const [selectedPeriod, setSelectedPeriod] = useParamsState('period', 'ytd');
    const [minPlayers, setMinPlayers] = useParamsState('min_players', 10);
    const [page, setPage] = useState(0);
    const [clanCount, setClanCount] = useState(0);
    const [highlightRowIndex, setHighlightRowIndex] = useState(null);
    const [force, setForce] = useState(0);
    const LEADERBOARD_PERIOD_TITLE = (PERIODS[selectedPeriod].label + " " + STRINGS.REGIMENT_LEADERBOARD).toUpperCase();
    const showJumpToClan = myClanPage !== null && page !== myClanPage;

    const stateManager = {
        'players_active': {value: minPlayers, setter: setMinPlayers}
    }
    
    useEffect(() => {
        var clanData = [];
        Promise.all([getClanLeaderboard(selectedPeriod, sortColumn, sortAscending, page, minPlayers)])
            .then(results => {
                clanData = results[0].data;
                setClanCount(clanData.count);
                setClans(clanData.clans);
                setMyClan(clanData.myClan);
                setMyClanPage(clanData.myClanPage);
                setHighlightRowIndex(null);
            })
            .catch((err) => {
                console.log(err);
            });
        }, [page, minPlayers, force]);

    useEffect(() => {
        if(page === 0) {
            setForce(force + 1);
        } else {
            setPage(0);
        }
    }, [sortColumn, selectedPeriod, sortAscending, minPlayers]);

    useEffect(() => {
        setMinPlayers(10);
    }, [selectedPeriod]);

    const maxPages = useMemo(() => {
        return Math.ceil(clanCount / CLANS_PER_PAGE);
    }, [clanCount]);

    const blankField = Array.from({ length: Object.keys(LEADERBOARD_COLUMNS).length + 1 }, (_, index) => (
        <td className={'sticky ' + (index === 0 ? 'rank' : index === 1 ? 'name' : '')} key={index}></td>
    ));

    const blankRow = (
        <tr>
            { blankField }
        </tr>
    );

    const jumpToClan = showJumpToClan ? (
        <>
            <span className="orange-text" onClick={ () => setPage(myClanPage) }>{STRINGS.JUMP_TO_CLAN} ↷</span>
        </>
    ) : <br/>;

    const myClanRow = myClan && showJumpToClan ? (
        <LeaderboardTableRow 
            rank={myClan.rank} 
            clan={myClan} 
            selectedColumn={sortColumn} 
            selectedPeriod={selectedPeriod} 
            highlightRow={true} />
    ) : null;

    const determineRank = (index) => 
        !sortColumn ? null : 
        sortAscending ? clanCount - ((page * CLANS_PER_PAGE) + index) : 
        1 + (page * CLANS_PER_PAGE) + index;

    const isHighlightRow = (index, clan_id) => index === highlightRowIndex || myClan?.clan_db_id === clan_id;

    return (
        <section>
            <h2>{LEADERBOARD_PERIOD_TITLE}</h2>
            <PeriodFilter selectedPeriod={selectedPeriod} setSelectedPeriod={setSelectedPeriod}/>
            { Object.keys(FILTERS).length > 0 && Object.keys(stateManager).length > 0 &&
                (Object.keys(stateManager)
                    .filter(key => FILTERS.hasOwnProperty(key))
                    .map((key, index) => (
                        <StatFilter key={index} index={index} config={FILTERS[key]} value={stateManager[key].value} setter={stateManager[key].setter}/>
                    ))
            )}
            { jumpToClan }
            <div className="clanleaderboard_table_overflow_wrapper">
            <table className="clanleaderboard_table">
                <thead>
                    <LeaderboardTableHeaderRow sortColumn={sortColumn} setSortColumn={setSortColumn} sortAscending={sortAscending} setSortAscending={setSortAscending}/>
                </thead>
                <tbody>
                    { myClanPage < page && myClanRow }
                    {clans.length > 0 ? 
                        ( clans.map((clan, index) => (
                        <LeaderboardTableRow 
                            key={index} 
                            rank={determineRank(index)} 
                            clan={clan} 
                            selectedPeriod={selectedPeriod} 
                            selectedColumn={sortColumn} 
                            highlightRow={isHighlightRow(index, clan.clan_db_id)} 
                            onClick={() => highlightRowIndex === index ? setHighlightRowIndex(null) : setHighlightRowIndex(index)} />)
                            )
                        ) : (
                            blankRow
                        )}
                    { myClanPage > page && myClanRow }
                </tbody>
            </table>
            </div>
        { maxPages > 0 && <Pagination page={page} setPage={setPage} maxPages={maxPages} /> }
        </section>
    )
}

export default LeaderboardTable;
